import { useEffect, useState } from "react";
import { Cpu, Radar, Zap, Radio, Activity } from "lucide-react";
import { api } from "@/lib/api";
import { relativeTime } from "@/lib/format";

const STAGE_META = {
  ingestion: { label: "Ingestion", icon: Activity, color: "#38BDF8", desc: "Fetch · timestamp · tag source" },
  normalization: { label: "Normalization", icon: Radar, color: "#A855F7", desc: "Resolve identity · dedupe · UTC" },
  classification: { label: "Classification", icon: Zap, color: "#FFB020", desc: "Tag type · assign certainty" },
  relevance_alert: { label: "Relevance / Alert", icon: Radio, color: "#00E5A3", desc: "Match watchlists · push alerts" },
};

export default function PipelineTelemetry() {
  const [snap, setSnap] = useState(null);

  useEffect(() => {
    const load = async () => { try { const r = await api.get("/pipeline"); setSnap(r.data); } catch { /* telemetry poll failed; keep last snapshot */ } };
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="max-w-[1400px] mx-auto p-4 sm:p-6">
      <div className="mb-6">
        <p className="font-mono text-xs uppercase tracking-widest text-[#00E5A3] mb-1">System Monitor</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-100">Multi-Agent Pipeline Telemetry</h1>
        <p className="text-sm text-slate-400 mt-1">Live throughput & latency across ingestion agents and the normalization → classification → alert pipeline.</p>
      </div>

      {!snap ? <div className="text-slate-500 font-mono">Loading telemetry…</div> : (
        <>
          {/* summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
            <Metric label="Total Events" value={snap.total_events} accent />
            <Metric label="Ingestion Agents" value={snap.agents.length} />
            <Metric label="Pipeline Stages" value={snap.stages.length} />
            <Metric label="Avg Latency" value={`${avg(snap.stages)}ms`} />
          </div>

          {/* stages */}
          <h2 className="font-heading text-lg font-semibold text-slate-100 mb-3">Processing Stages</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {snap.stages.map((s, i) => {
              const m = STAGE_META[s.key];
              const Icon = m.icon;
              return (
                <div key={s.key} data-testid={`telemetry-stage-${s.key}`} className="relative bg-[#0F1420] border border-[#232D42] rounded-xl p-5 overflow-hidden">
                  <div className="absolute top-0 left-0 h-0.5 w-full" style={{ background: m.color, opacity: s.status === "active" ? 0.8 : 0.2 }} />
                  <div className="flex items-center justify-between mb-3">
                    <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: `${m.color}18` }}><Icon size={18} style={{ color: m.color }} /></div>
                    <span className="font-mono text-xs text-slate-600">0{i + 1}</span>
                  </div>
                  <h3 className="font-heading font-semibold text-slate-100">{m.label}</h3>
                  <p className="text-xs text-slate-500 mt-0.5 font-mono">{m.desc}</p>
                  <div className="mt-4 space-y-1.5">
                    <Row k="Processed" v={s.processed} />
                    <Row k="Avg latency" v={`${s.avg_latency_ms}ms`} />
                    <Row k="Last active" v={s.last_active ? relativeTime(s.last_active) : "—"} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* agents */}
          <h2 className="font-heading text-lg font-semibold text-slate-100 mb-3">Ingestion Agents</h2>
          <div className="bg-[#0F1420] border border-[#232D42] rounded-xl overflow-hidden">
            <div className="grid grid-cols-12 gap-2 px-5 py-3 border-b border-[#232D42] text-[11px] font-mono uppercase tracking-wider text-slate-500">
              <div className="col-span-5 sm:col-span-4">Agent</div>
              <div className="col-span-4 sm:col-span-3 hidden sm:block">Source</div>
              <div className="col-span-3 sm:col-span-2">Schedule</div>
              <div className="col-span-2 text-right">Processed</div>
              <div className="col-span-2 sm:col-span-1 text-right">Latency</div>
            </div>
            {snap.agents.map((a) => (
              <div key={a.id} data-testid={`telemetry-agent-${a.id}`} className="grid grid-cols-12 gap-2 px-5 py-3.5 border-b border-[#232D42]/50 items-center hover:bg-[#151B2B]">
                <div className="col-span-5 sm:col-span-4 flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${a.status === "active" ? "bg-[#00E5A3] sq-pulse" : "bg-slate-600"}`} />
                  <span className="text-sm text-slate-200 font-medium truncate">{a.name}</span>
                </div>
                <div className="col-span-3 hidden sm:block text-xs text-slate-400 font-mono truncate">{a.source}</div>
                <div className="col-span-3 sm:col-span-2 text-xs text-slate-500 font-mono">{a.interval}</div>
                <div className="col-span-2 text-right font-mono text-sm text-slate-200">{a.processed}</div>
                <div className="col-span-2 sm:col-span-1 text-right font-mono text-xs text-[#00E5A3]">{a.avg_latency_ms}ms</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const avg = (stages) => {
  if (!stages.length) return 0;
  return Math.round(stages.reduce((s, x) => s + x.avg_latency_ms, 0) / stages.length);
};

function Metric({ label, value, accent }) {
  return (
    <div className="bg-[#0F1420] border border-[#232D42] rounded-xl p-4">
      <p className="text-xs font-mono uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`font-heading text-2xl font-extrabold mt-1 ${accent ? "text-[#00E5A3]" : "text-slate-100"}`}>{value}</p>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between text-xs font-mono">
      <span className="text-slate-500">{k}</span><span className="text-slate-300">{v}</span>
    </div>
  );
}
